// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore mustache is in the parent dir
import Mustache from 'mustache';

import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import {
  copyToClipboard,
  createRichTextContent,
  isHTMLContent,
} from 'utils/copyToClipboard';
import { formatCurrency } from 'utils/formatCurrency';

import {
  CarrierCostType,
  LTLQuote,
  LTLQuoteLaneAddress,
  QuoteFormStop,
  QuoteMode,
} from './types';
import { toTitleCase } from './utils/formatting';
import { parseLocation } from './utils/location';

const roundMoney = (n: number): number => Math.round(n * 100) / 100;

// Options for {@link buildPortalSubmitBidAmountStrings}.
export type BuildPortalSubmitBidAmountStringsOptions = {
  quoteMode: QuoteMode;
  fuelEstimate?: Undef<number>;
  ltlQuote?: Undef<LTLQuote>;
};

// String amounts for Drumkit `POST /integrations/portal/submit-bid` (see PickPortalBidAmount).
export type PortalSubmitBidAmountStrings = {
  amount: string;
  totalSellAmount: string;
  lineHaulAmount: string;
};

/**
 * Builds `amount` / `totalSellAmount` / `lineHaulAmount` for Drumkit
 * `POST /integrations/portal/submit-bid` (see PickPortalBidAmount).
 *
 * `totalSell` should match the all-in sell the user is submitting. Line haul subtracts
 * FTL fuel estimate (DAT-style) or LTL sell fuel surcharge when present; otherwise it
 * equals total so `line_haul` bid basis still receives a value.
 */
export function buildPortalSubmitBidAmountStrings(
  totalSell: number,
  options: BuildPortalSubmitBidAmountStringsOptions
): PortalSubmitBidAmountStrings {
  const roundedTotal = roundMoney(totalSell);
  let lineHaul = roundedTotal;

  if (options.quoteMode === QuoteMode.LTL && options.ltlQuote) {
    const fuel = options.ltlQuote.rateMetadata?.sellFuelSurcharge;
    if (typeof fuel === 'number' && fuel > 0 && Number.isFinite(fuel)) {
      lineHaul = roundMoney(Math.max(0, roundedTotal - fuel));
    }
  } else if (
    options.quoteMode === QuoteMode.TL &&
    typeof options.fuelEstimate === 'number' &&
    options.fuelEstimate > 0
  ) {
    lineHaul = roundMoney(Math.max(0, roundedTotal - options.fuelEstimate));
  }

  const totalStr = String(roundedTotal);
  const lineHaulStr = String(lineHaul);
  const result: PortalSubmitBidAmountStrings = {
    amount: totalStr,
    totalSellAmount: totalStr,
    lineHaulAmount: lineHaulStr,
  };
  return result;
}

/** Options for {@link formatLTLDraftTemplate}; defaults preserve legacy whole-dollar rate display for non-3G integrations. */
export type LTLDraftTemplateFormatOptions = {
  /** Passed as `formatCurrency` max fraction digits for each quote `rate` (sell/total). */
  rateFractionDigits?: number;
  /** Hides Tai's limited liability line from the draft response. */
  hideTaiLimitedLiability?: boolean;
};

// Geo search stores the selected option id as gplace:… on stop.location.
const isGeoLocationId = (value: string): boolean =>
  value.trim().startsWith('gplace:');

type ResolvedLTLDraftCityState = {
  city: string;
  state: string;
  zip: string;
};

function resolveLTLDraftCityState(
  stop: QuoteFormStop,
  lane: LTLQuoteLaneAddress
): ResolvedLTLDraftCityState {
  const parsed = parseLocation((stop.location ?? '').trim());

  const resolved: ResolvedLTLDraftCityState = {
    city: (stop.city || parsed?.city || lane.city || '').trim(),
    state: (stop.state || parsed?.state || lane.state || '').trim(),
    zip: (stop.zip || parsed?.zip || lane.zip || '').trim(),
  };

  return resolved;
}

/**
 * Endpoint label for the LTL draft. Prefers explicit display fields from the form, then falls back
 * to city/state merged from the stop, parsed location text, and provider-normalized quote lane.
 */
export function formatLTLDraftLocation(
  stop: QuoteFormStop,
  lane: LTLQuoteLaneAddress,
  fallback: string
): string {
  const locationDisplay = (stop.locationDisplay ?? '').trim();
  if (locationDisplay) {
    return locationDisplay;
  }

  const formCity = (stop.city ?? '').trim();
  const formState = (stop.state ?? '').trim();
  if (formCity && formState) {
    return `${toTitleCase(formCity)}, ${formState}`;
  }

  if (formCity) {
    return toTitleCase(formCity);
  }

  const addressName = (stop.addressName ?? '').trim();
  if (addressName) {
    return addressName;
  }

  const location = (stop.location ?? '').trim();
  if (location && !isGeoLocationId(location)) {
    const parsedLocation = parseLocation(location);
    const isZipOnlyLocation =
      !!parsedLocation?.zip && !parsedLocation.city && !parsedLocation.state;
    const canHydrateFromLane =
      Boolean((lane.city ?? '').trim()) && Boolean((lane.state ?? '').trim());

    if (!isZipOnlyLocation || !canHydrateFromLane) {
      return location;
    }
  }

  const resolved = resolveLTLDraftCityState(stop, lane);
  if (resolved.city && resolved.state) {
    return `${toTitleCase(resolved.city)}, ${resolved.state.toUpperCase()}`;
  }

  if (resolved.zip) {
    return resolved.zip;
  }

  if (resolved.city) {
    return toTitleCase(resolved.city);
  }

  return fallback;
}

function hasLTLCoverageAmount(amount: MaybeUndef<number>): boolean {
  return amount != null && amount !== 0;
}

function formatLTLCoverageAmount(amount: MaybeUndef<number>): string {
  if (amount == null || amount === 0) {
    return '';
  }

  return formatCurrency(amount, 'USD');
}

export function getLTLSellRate(quote: LTLQuote): number {
  if (
    quote.rateMetadata?.isTurvo &&
    typeof quote.rateMetadata.billable === 'number'
  ) {
    return quote.rateMetadata.billable;
  }

  const sellTotal = quote.rateMetadata?.sellRateTotal;
  if (sellTotal && !Number.isNaN(sellTotal) && sellTotal > 0) {
    return sellTotal;
  }

  // Fall back to total cost if sell rate is not available
  return quote.totalCost;
}

export function getLTLQuoteIdentity(quote: LTLQuote): string {
  if (quote.id > 0) {
    return `id:${quote.id}`;
  }

  const pricingType =
    quote.rateMetadata?.pricingType?.trim().toLowerCase() ?? '';

  return [
    quote.externalID ?? '',
    quote.carrierName ?? '',
    quote.carrierSCAC ?? '',
    quote.totalCost.toFixed(2),
    pricingType,
  ]
    .map((part) => String(part).trim().toLowerCase())
    .join('|');
}

/**
 * Fills sell-price placeholders in a pre-rendered QuickQuoteReply body.
 * Production API (get_quick_quote_v2.go) leaves "{{rate}}"; older harness/fixture templates
 * used "{{finalPrice}}". Both receive the same formatted sell string.
 */
export function fillDraftRate(preRenderedBody: string, rate: string): string {
  return Mustache.render(preRenderedBody, { rate, finalPrice: rate });
}

/**
 * Swaps the sell rate inside an existing draft body without touching other content.
 * Uses the last occurrence of `previousRate` so static amounts like "$100k" are not
 * corrupted when intermediate prices (e.g. "$1") are a prefix of them.
 * Falls back to filling `{{rate}}` / `{{finalPrice}}` if a placeholder is still present.
 */
export function swapDraftRateInBody(
  body: string,
  previousRate: string,
  nextRate: string
): string {
  if (body.includes('{{rate}}') || body.includes('{{finalPrice}}')) {
    return fillDraftRate(body, nextRate);
  }

  if (!previousRate || previousRate === nextRate) {
    return body;
  }

  const lastIndex = body.lastIndexOf(previousRate);
  if (lastIndex === -1) {
    return body;
  }

  return (
    body.slice(0, lastIndex) +
    nextRate +
    body.slice(lastIndex + previousRate.length)
  );
}

export type LTLQuoteTemplateView = ReturnType<typeof formatLTLQuoteForTemplate>;

type LTLQuoteTemplateViewOptions = {
  rateFractionDigits: number;
  hideLimitedLiability: boolean;
};

// Mustache view for one LTL quote; shared by the single and batch LTL reply templates.
export function formatLTLQuoteForTemplate(
  quote: LTLQuote,
  { rateFractionDigits, hideLimitedLiability }: LTLQuoteTemplateViewOptions
) {
  const hasCoverageAmountNew = hasLTLCoverageAmount(quote.coverageAmountNew);
  const hasCoverageAmountUsed = hasLTLCoverageAmount(quote.coverageAmountUsed);
  const coverageAmountNew = formatLTLCoverageAmount(quote.coverageAmountNew);
  const coverageAmountUsed = formatLTLCoverageAmount(quote.coverageAmountUsed);
  const hasLiability =
    !hideLimitedLiability && (hasCoverageAmountNew || hasCoverageAmountUsed);
  // Legacy template support: coverageType or new amount only
  const hasInsurance =
    !hideLimitedLiability &&
    (Boolean(quote.coverageType?.trim()) || hasCoverageAmountNew);

  return {
    carrierName: quote.carrierName,
    rate: formatCurrency(getLTLSellRate(quote), 'USD', rateFractionDigits),
    rateAmount: getLTLSellRate(quote),
    transitDays: quote.transitDays,
    transitDaysText:
      quote.transitDays === 1
        ? '1 business day'
        : `${quote.transitDays} business days`,
    transitDaysRangeText: formatTransitDaysRange(quote.transitDays),
    serviceLevel: quote.serviceLevel || '',
    hasServiceLevel: Boolean(quote.serviceLevel),
    guaranteed: quote.guaranteed,
    guaranteedText: quote.guaranteed ? 'Guaranteed' : '',
    coverageType: quote.coverageType || '',
    coverageAmountNew,
    coverageAmountUsed,
    hasCoverageAmountNew,
    hasCoverageAmountUsed,
    hasLiability,
    // Backward-compatible aliases for older templates
    coverageAmount: coverageAmountNew,
    hasInsurance,
  };
}

function formatTransitDaysRange(days: number): string {
  if (!Number.isFinite(days) || days <= 0) {
    return 'N/A';
  }

  const roundedDays = Math.max(1, Math.floor(days));
  return `${roundedDays}-${roundedDays + 2} business days`;
}

/**
 * Formats an LTL draft template with selected quotes
 */
export function formatLTLDraftTemplate(
  templateBody: string,
  pickupLocation: string,
  deliveryLocation: string,
  pickupDate: Undef<Date>,
  deliveryDate: Undef<Date>,
  selectedQuotes: LTLQuote[],
  externalQuoteID: Maybe<string>,
  externalQuoteSource: Maybe<string>,
  formatOptions?: LTLDraftTemplateFormatOptions
): string {
  const hasAnyAccessorialValue = (value: unknown): boolean => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value !== null && value !== undefined && value !== '';
  };

  const stringifyPickupService = (value: unknown): string => {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (Array.isArray(value)) {
      return value.map(stringifyPickupService).filter(Boolean).join(', ');
    }

    if (value && typeof value === 'object') {
      const objectValues = Object.values(value as Record<string, unknown>);
      return objectValues
        .map(stringifyPickupService)
        .filter(Boolean)
        .join(' - ');
    }

    return '';
  };

  const rateFractionDigits = formatOptions?.rateFractionDigits ?? 0;

  // Validate and clean dates
  if (pickupDate && isNaN(pickupDate.getTime())) {
    pickupDate = undefined;
  }
  if (deliveryDate && isNaN(deliveryDate.getTime())) {
    deliveryDate = undefined;
  }

  const isTurvoIntegration =
    externalQuoteSource?.toLowerCase() === TMS.Turvo ||
    selectedQuotes.some((quote) => quote.rateMetadata?.isTurvo);
  const hideTaiLimitedLiability =
    externalQuoteSource?.toLowerCase() === TMS.Tai &&
    formatOptions?.hideTaiLimitedLiability === true;

  // Format each quote for the template
  const quotes = selectedQuotes.map((quote) =>
    formatLTLQuoteForTemplate(quote, {
      rateFractionDigits,
      hideLimitedLiability: hideTaiLimitedLiability,
    })
  );

  // Sort by price (cheapest first)
  quotes.sort((a, b) => a.rateAmount - b.rateAmount);

  const quoteWithAccessorials = selectedQuotes.find((quote) => {
    const pickupServices = quote.rateMetadata?.pickupServices;
    const deliveryServices = quote.rateMetadata?.deliveryServices;

    return (
      hasAnyAccessorialValue(pickupServices) ||
      hasAnyAccessorialValue(deliveryServices)
    );
  });
  const pickupServices = quoteWithAccessorials?.rateMetadata?.pickupServices;
  const deliveryServices =
    quoteWithAccessorials?.rateMetadata?.deliveryServices;
  const accessorialsSource = [
    hasAnyAccessorialValue(pickupServices) ? pickupServices : undefined,
    hasAnyAccessorialValue(deliveryServices) ? deliveryServices : undefined,
  ].filter((value) => value !== undefined);
  const accessorialsText = stringifyPickupService(accessorialsSource);
  const hasAccessorials = isTurvoIntegration && !!accessorialsText;
  const shouldShowQuoteID = externalQuoteSource?.toLowerCase() === TMS.Tai;

  const providerLabel = shouldShowQuoteID ? 'Tai Quote #' : 'Quote Number';
  const hasExternalQuoteID = Boolean(externalQuoteID);

  const rendered = Mustache.render(templateBody, {
    // Mustache template variables must match exact variable names in the template defined in BE
    pickupLocation,
    deliveryLocation,
    pickupDate: pickupDate?.toDateString(),
    deliveryDate: deliveryDate?.toDateString(),
    hasPickupDate: !!pickupDate,
    hasDeliveryDate: !!deliveryDate,
    quotes,
    quoteCount: quotes.length,
    isMultipleQuotes: quotes.length > 1,
    isSingleQuote: quotes.length === 1,
    isTurvoIntegration,
    hasAccessorials,
    accessorialsText,
    externalQuoteID,
    hasExternalQuoteID,
    shouldShowQuoteID,
    externalQuoteLabel: providerLabel,
  });

  return rendered;
}

/**
 * Formats the final price in the draft response based on the selected format
 */
export function formatFinalPriceInDraft(
  flatPrice: Maybe<number>,
  format: CarrierCostType | 'Both',
  maxDistance: number,
  fuelEstimate?: number,
  formatAmount?: (amount: number, maxFractionDigits?: number) => string
): string {
  const formatUsd = (amount: number, maxFractionDigits = 0) => {
    if (formatAmount) {
      return formatAmount(amount, maxFractionDigits);
    }

    return formatCurrency(amount, 'USD', maxFractionDigits);
  };

  const price = flatPrice ?? 0;
  if (format === CarrierCostType.Linehaul && fuelEstimate) {
    return `${formatUsd(price - fuelEstimate)}`;
  }

  if (!maxDistance) {
    return formatUsd(price);
  }

  if (format === 'Both') {
    return `${formatUsd(price)} (${formatUsd(price / maxDistance, 2)}/mile)`;
  } else if (format === CarrierCostType.PerMile) {
    return `${formatUsd(price / maxDistance, 2)}/mile`;
  } else {
    return formatUsd(price);
  }
}

/**
 * Handles copying the draft response to clipboard
 */
export async function handleCopyToClipboard(
  draftResponse: string,
  onSuccess?: () => void
): Promise<boolean> {
  const success = await copyToClipboard(
    isHTMLContent(draftResponse)
      ? createRichTextContent(draftResponse)
      : draftResponse,
    { showToast: false } // Don't show toast since we already show tooltip
  );

  if (success && onSuccess) {
    onSuccess();
  }

  return success;
}
